/**
 * Mark up a screenshot in place, before it is attached to a reported Issue: Box, Arrow,
 * Text and Redact, with Select to move, recolour or delete, Undo / Redo, and Clear all.
 *
 * Not a pop-up window. A capture is laid over the page at exactly the size it was taken
 * (src/lib/markup/sizing.ts `fitContain`), so the screen appears to freeze; a thin frame
 * round the edge and a floating toolbar (MarkupToolbar) are the only chrome, and the
 * reporter draws straight onto what they were looking at. An uploaded image isn't a
 * picture of this screen, so it is shown centred on a dimmed backdrop instead.
 *
 * Underneath it is still a focus-trapped layer — Escape cancels, and keyboard and
 * screen-reader users stay inside it. Works by mouse, touch and pen: the drawing surface
 * takes `touch-action: none`, so a drag draws instead of scrolling. Every coordinate is
 * in the image's natural pixels (src/lib/markup/marks.ts), so what is drawn here lands in
 * the same place on the export.
 *
 * Text is typed where it goes: placing a Text mark opens a field just under it and the
 * words appear on the image as you type. The toolbar's notes list is how a keyboard or
 * screen-reader user reads and corrects them.
 *
 * Cancel leaves the marks as they were before the editor opened; Done hands back the new
 * list. Nothing is flattened here — the parent burns the marks in at submit.
 */

import { Trash2 } from "lucide-react";
import { Dialog as DialogPrimitive } from "radix-ui";
import { type PointerEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  canRedo,
  canUndo,
  commit,
  createHistory,
  type MarkHistory,
  redo,
  undo,
} from "@/lib/markup/history";
import {
  arrowHead,
  clampPoint,
  DEFAULT_MARK_COLOR,
  isMeaningful,
  type Mark,
  type MarkColor,
  type MarkTool,
  markAt,
  markStyle,
  newMarkId,
  normalizeRect,
  type Point,
  recolorMark,
  textBounds,
  translateMark,
} from "@/lib/markup/marks";
import { fitContain, type Size, screenToImage } from "@/lib/markup/sizing";
import { CAPTURE_IGNORE_PROPS } from "@/lib/screenshot-capture";
import { cn } from "@/lib/utils";
import { MARKUP_TOOLS, MarkupToolbar, penVar, type ToolbarPosition } from "./MarkupToolbar";

interface MarkupEditorProps {
  open: boolean;
  /** A capture of this screen is shown in place; an upload is centred on a backdrop. */
  origin: "capture" | "upload";
  imageUrl: string;
  width: number;
  height: number;
  initialMarks: Mark[];
  onDone: (marks: Mark[]) => void;
  onCancel: () => void;
}

/** Room kept round an uploaded image for the floating toolbar. */
const UPLOAD_INSET = { x: 16, y: 72 };
const EMPTY_NOTE = "Type your note…";

type Gesture =
  | { kind: "draw"; markId: string; start: Point }
  | { kind: "move"; markId: string; last: Point; moved: boolean };

/** A note being typed, and where its field is: under the mark, or in the notes list. */
interface Editing {
  id: string;
  where: "inline" | "list";
}

function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

function windowSize(): Size {
  return { width: window.innerWidth, height: window.innerHeight };
}

export function MarkupEditor({
  open,
  origin,
  imageUrl,
  width,
  height,
  initialMarks,
  onDone,
  onCancel,
}: MarkupEditorProps) {
  const style = useMemo(() => markStyle(width, height), [width, height]);
  const [history, setHistoryState] = useState<MarkHistory>(() => createHistory(initialMarks));
  /** Marks mid-gesture or mid-typing; shown instead of `history.present` until committed. */
  const [draft, setDraftState] = useState<Mark[] | null>(null);
  const [editing, setEditingState] = useState<Editing | null>(null);
  const [tool, setTool] = useState<MarkTool>("box");
  const [color, setColor] = useState<MarkColor>(DEFAULT_MARK_COLOR);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewport, setViewport] = useState<Size>(windowSize);
  const [toolbarAt, setToolbarAt] = useState<ToolbarPosition>("top");
  const [notesOpen, setNotesOpen] = useState(false);

  // Pointer and focus events can fire back to back inside one frame (a press on the image
  // blurs the note being typed), so the editing state is read and written through refs —
  // every handler sees what the previous one did, and a note is committed exactly once.
  const historyRef = useRef(history);
  const draftRef = useRef<Mark[] | null>(null);
  const editingRef = useRef<Editing | null>(null);

  const setHistory = useCallback((next: MarkHistory) => {
    historyRef.current = next;
    setHistoryState(next);
  }, []);
  const setDraft = useCallback((next: Mark[] | null) => {
    draftRef.current = next;
    setDraftState(next);
  }, []);
  const setEditing = useCallback((next: Editing | null) => {
    editingRef.current = next;
    setEditingState(next);
  }, []);
  const commitMarks = useCallback(
    (next: Mark[]) => setHistory(commit(historyRef.current, next)),
    [setHistory],
  );

  const svgRef = useRef<SVGSVGElement | null>(null);
  const gesture = useRef<Gesture | null>(null);
  const inlineInput = useRef<HTMLInputElement | null>(null);

  // Fresh state each time the editor opens on a (possibly different) screenshot.
  useEffect(() => {
    if (!open) return;
    setHistory(createHistory(initialMarks));
    setDraft(null);
    setEditing(null);
    setSelectedId(null);
    setNotesOpen(false);
    setTool(initialMarks.length > 0 ? "select" : "box");
  }, [open, initialMarks, setHistory, setDraft, setEditing]);

  // Keep the image placed over the window as it resizes.
  useEffect(() => {
    if (!open) return;
    const onResize = () => setViewport(windowSize());
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [open]);

  const placement = useMemo(() => {
    if (origin === "capture") return fitContain({ width, height }, viewport);
    const inner = fitContain(
      { width, height },
      {
        width: Math.max(1, viewport.width - UPLOAD_INSET.x * 2),
        height: Math.max(1, viewport.height - UPLOAD_INSET.y * 2),
      },
    );
    return { ...inner, left: inner.left + UPLOAD_INSET.x, top: inner.top + UPLOAD_INSET.y };
  }, [origin, width, height, viewport]);
  /** Screen px per image px. */
  const displayScale = placement.width / Math.max(width, 1);

  const marks = draft ?? history.present;
  const selected = marks.find((m) => m.id === selectedId) ?? null;
  const textMarks = marks.filter((m): m is Extract<Mark, { kind: "text" }> => m.kind === "text");
  const inlineMark =
    editing?.where === "inline" ? textMarks.find((m) => m.id === editing.id) : undefined;

  const toImage = useCallback(
    (clientX: number, clientY: number): Point => {
      const svg = svgRef.current;
      if (!svg) return { x: 0, y: 0 };
      const r = svg.getBoundingClientRect();
      const p = screenToImage(
        { x: clientX, y: clientY },
        { left: r.left, top: r.top, width: r.width, height: r.height },
        { width, height },
      );
      return clampPoint(p, width, height);
    },
    [width, height],
  );

  /**
   * Finish typing a note — commit it, or drop it when left empty — and return the marks
   * as committed. Safe to call twice: the second call finds nothing being edited.
   */
  const settle = useCallback((): Mark[] => {
    const id = editingRef.current?.id ?? null;
    const present = historyRef.current.present;
    if (id === null) return present;
    const current = draftRef.current ?? present;
    const mark = current.find((m) => m.id === id);
    const keep = mark !== undefined && mark.kind === "text" && isMeaningful(mark, style);
    const next = keep ? current : present.filter((m) => m.id !== id);
    const unchanged = next.length === present.length && next.every((m, i) => m === present[i]);
    if (!unchanged) commitMarks(next);
    setDraft(null);
    setEditing(null);
    if (!keep) setSelectedId((s) => (s === id ? null : s));
    return next;
  }, [style, commitMarks, setDraft, setEditing]);

  const resetTransient = () => {
    setDraft(null);
    setEditing(null);
    setSelectedId(null);
  };

  const doUndo = () => {
    settle();
    resetTransient();
    setHistory(undo(historyRef.current));
  };
  const doRedo = () => {
    settle();
    resetTransient();
    setHistory(redo(historyRef.current));
  };

  const deleteSelected = () => {
    const base = settle();
    if (!selectedId) return;
    commitMarks(base.filter((m) => m.id !== selectedId));
    setSelectedId(null);
  };

  const clearAll = () => {
    const base = settle();
    setSelectedId(null);
    if (base.length > 0) commitMarks([]);
  };

  const pickColor = (next: MarkColor) => {
    setColor(next);
    const base = settle();
    const target = base.find((m) => m.id === selectedId);
    if (target && target.kind !== "redact" && target.color !== next) {
      commitMarks(base.map((m) => (m.id === target.id ? recolorMark(m, next) : m)));
    }
  };

  const pickTool = (next: MarkTool) => {
    settle();
    setTool(next);
    if (next !== "select") setSelectedId(null);
  };

  const startEditingText = (id: string, base: Mark[], where: Editing["where"]) => {
    setDraft(base);
    setEditing({ id, where });
    setSelectedId(id);
    if (where === "inline") requestAnimationFrame(() => inlineInput.current?.focus());
  };

  const typeNote = (id: string, text: string) => {
    const base = draftRef.current ?? historyRef.current.present;
    setDraft(base.map((x) => (x.id === id && x.kind === "text" ? { ...x, text } : x)));
  };

  const deleteNote = (id: string) => {
    const base = settle();
    setSelectedId(null);
    if (base.some((x) => x.id === id)) commitMarks(base.filter((x) => x.id !== id));
  };

  const finish = () => onDone(settle().filter((m) => isMeaningful(m, style)));

  // ── Pointer handling ───────────────────────────────────────────────────────

  const onPointerDown = (e: PointerEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg || !e.isPrimary) return;
    svg.setPointerCapture(e.pointerId);

    const p = toImage(e.clientX, e.clientY);
    const base = settle();

    if (tool === "select") {
      const hit = markAt(base, p, style);
      setSelectedId(hit?.id ?? null);
      if (hit) {
        if (hit.kind !== "redact") setColor(hit.color);
        gesture.current = { kind: "move", markId: hit.id, last: p, moved: false };
        setDraft(base);
      }
      return;
    }

    if (tool === "text") {
      // A press on an existing note edits it; anywhere else places a new one.
      const hit = markAt(base, p, style);
      if (hit?.kind === "text") {
        startEditingText(hit.id, base, "inline");
        return;
      }
      const id = newMarkId();
      startEditingText(
        id,
        [...base, { id, kind: "text", color, x: p.x, y: p.y, text: "" }],
        "inline",
      );
      return;
    }

    const id = newMarkId();
    const mark: Mark =
      tool === "arrow"
        ? { id, kind: "arrow", color, x1: p.x, y1: p.y, x2: p.x, y2: p.y }
        : tool === "redact"
          ? { id, kind: "redact", x: p.x, y: p.y, w: 0, h: 0 }
          : { id, kind: "box", color, x: p.x, y: p.y, w: 0, h: 0 };
    gesture.current = { kind: "draw", markId: id, start: p };
    setSelectedId(null);
    setDraft([...base, mark]);
  };

  const onPointerMove = (e: PointerEvent<SVGSVGElement>) => {
    if (!e.isPrimary) return;
    const g = gesture.current;
    const current = draftRef.current;
    if (!g || !current) return;
    const p = toImage(e.clientX, e.clientY);
    if (g.kind === "draw") {
      setDraft(
        current.map((m) => {
          if (m.id !== g.markId) return m;
          if (m.kind === "arrow") return { ...m, x2: p.x, y2: p.y };
          if (m.kind === "box" || m.kind === "redact")
            return { ...m, ...normalizeRect(g.start, p) };
          return m;
        }),
      );
      return;
    }

    const dx = p.x - g.last.x;
    const dy = p.y - g.last.y;
    if (dx === 0 && dy === 0) return;
    gesture.current = { ...g, last: p, moved: true };
    setDraft(current.map((m) => (m.id === g.markId ? translateMark(m, dx, dy) : m)));
  };

  const endGesture = (e: PointerEvent<SVGSVGElement>) => {
    if (!e.isPrimary) return;
    const g = gesture.current;
    if (!g) return;
    gesture.current = null;
    const current = draftRef.current;
    setDraft(null);
    if (!current) return;
    if (g.kind === "draw") {
      const mark = current.find((m) => m.id === g.markId);
      if (mark && isMeaningful(mark, style)) commitMarks(current);
      return;
    }
    if (g.moved) commitMarks(current);
  };

  // ── Keyboard ───────────────────────────────────────────────────────────────

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (isTypingTarget(e.target)) return;
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === "z") {
      e.preventDefault();
      if (e.shiftKey) doRedo();
      else doUndo();
    } else if (mod && e.key.toLowerCase() === "y") {
      e.preventDefault();
      doRedo();
    } else if ((e.key === "Delete" || e.key === "Backspace") && selectedId) {
      e.preventDefault();
      deleteSelected();
    }
  };

  // ── Rendering ──────────────────────────────────────────────────────────────

  const renderMark = (m: Mark) => {
    switch (m.kind) {
      case "redact":
        return (
          <rect
            key={m.id}
            x={m.x}
            y={m.y}
            width={m.w}
            height={m.h}
            style={{ fill: "var(--markup-redact)" }}
          />
        );
      case "box":
        return (
          <rect
            key={m.id}
            x={m.x}
            y={m.y}
            width={m.w}
            height={m.h}
            fill="none"
            strokeWidth={style.strokeWidth}
            strokeLinejoin="round"
            style={{ stroke: penVar(m.color) }}
          />
        );
      case "arrow": {
        const head = arrowHead({ x: m.x1, y: m.y1 }, { x: m.x2, y: m.y2 }, style.arrowHeadLength);
        return (
          <g key={m.id} style={{ stroke: penVar(m.color), fill: penVar(m.color) }}>
            <line
              x1={m.x1}
              y1={m.y1}
              x2={m.x2}
              y2={m.y2}
              strokeWidth={style.strokeWidth}
              strokeLinecap="round"
            />
            <polygon
              points={`${head.tip.x},${head.tip.y} ${head.left.x},${head.left.y} ${head.right.x},${head.right.y}`}
              strokeWidth={1}
            />
          </g>
        );
      }
      case "text": {
        const empty = m.text.length === 0;
        // Image-pixel text inside a fixed viewBox (like a chart label), sized by markStyle
        // so it matches the exported image — not DOM body text.
        return (
          <text
            key={m.id}
            x={m.x}
            y={m.y}
            fontSize={style.fontSize}
            fontWeight={700}
            dominantBaseline="hanging"
            paintOrder="stroke"
            strokeWidth={Math.max(3, style.fontSize * 0.2)}
            strokeLinejoin="round"
            opacity={empty ? 0.6 : 1}
            style={{
              fill: penVar(m.color),
              stroke: "var(--markup-text-halo)",
              fontFamily: "var(--font-sans)",
            }}
          >
            {(empty ? EMPTY_NOTE : m.text).split("\n").map((line, lineNo, lines) => (
              <tspan
                key={`${m.id}:${lines.slice(0, lineNo + 1).join("\n").length}`}
                x={m.x}
                dy={lineNo === 0 ? 0 : style.fontSize * 1.25}
              >
                {line}
              </tspan>
            ))}
          </text>
        );
      }
    }
  };

  const selectionBox = (() => {
    if (!selected) return null;
    const pad = style.strokeWidth * 2;
    const box =
      selected.kind === "arrow"
        ? normalizeRect({ x: selected.x1, y: selected.y1 }, { x: selected.x2, y: selected.y2 })
        : selected.kind === "text"
          ? textBounds({ ...selected, text: selected.text || EMPTY_NOTE }, style.fontSize)
          : selected;
    return (
      <rect
        x={box.x - pad}
        y={box.y - pad}
        width={box.w + pad * 2}
        height={box.h + pad * 2}
        fill="none"
        strokeWidth={Math.max(1.5, style.strokeWidth / 2)}
        strokeDasharray={`${style.strokeWidth * 2} ${style.strokeWidth * 2}`}
        style={{ stroke: "var(--ring)" }}
        pointerEvents="none"
      />
    );
  })();

  // The inline note field sits just under its mark's text, in screen px, kept on screen.
  const inlineField = (() => {
    if (!inlineMark) return null;
    const bounds = textBounds(
      { ...inlineMark, text: inlineMark.text || EMPTY_NOTE },
      style.fontSize,
    );
    const fieldWidth = 240;
    const left = Math.min(
      Math.max(8, placement.left + inlineMark.x * displayScale),
      viewport.width - fieldWidth - 8,
    );
    const below = placement.top + (bounds.y + bounds.h) * displayScale + 6;
    const top = below + 40 > viewport.height ? placement.top + bounds.y * displayScale - 42 : below;
    return { left, top, width: fieldWidth };
  })();

  const activeHint = MARKUP_TOOLS.find((t) => t.tool === tool)?.hint ?? "";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          {...CAPTURE_IGNORE_PROPS}
          onKeyDown={onKeyDown}
          // Escape while typing a note finishes the note; otherwise it cancels the markup.
          onEscapeKeyDown={(e) => {
            if (isTypingTarget(e.target)) {
              e.preventDefault();
              if (e.target instanceof HTMLElement) e.target.blur();
            }
          }}
          className={cn(
            "fixed inset-0 z-50 overflow-hidden outline-none",
            origin === "upload" &&
              "bg-[color-mix(in_oklab,var(--navy-900)_75%,transparent)] data-[state=open]:animate-in data-[state=open]:fade-in-0",
          )}
        >
          <DialogPrimitive.Title className="sr-only">Mark up the screenshot</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {activeHint}
          </DialogPrimitive.Description>

          {/* The screenshot, laid exactly where the screen was. */}
          <div
            className={cn("absolute", origin === "upload" && "shadow-2xl")}
            style={{
              left: placement.left,
              top: placement.top,
              width: placement.width,
              height: placement.height,
            }}
          >
            <img
              src={imageUrl}
              alt="Your screenshot"
              draggable={false}
              className="pointer-events-none absolute inset-0 size-full select-none"
            />
            <svg
              ref={svgRef}
              viewBox={`0 0 ${width} ${height}`}
              className={cn(
                "absolute inset-0 size-full touch-none select-none",
                tool === "select"
                  ? "cursor-default"
                  : tool === "text"
                    ? "cursor-text"
                    : "cursor-crosshair",
              )}
              role="img"
              aria-label={`Screenshot with ${marks.length} mark${marks.length === 1 ? "" : "s"}`}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endGesture}
              onPointerCancel={endGesture}
            >
              {marks.map(renderMark)}
              {selectionBox}
            </svg>
          </div>

          {/* "You're marking up a screenshot", without a window round it. */}
          {origin === "capture" && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0 ring-2 ring-primary ring-inset"
            />
          )}

          {inlineMark && inlineField && (
            <Input
              ref={inlineInput}
              aria-label="Note on the image"
              value={inlineMark.text}
              maxLength={500}
              placeholder={EMPTY_NOTE}
              className="absolute h-8 bg-card shadow-md"
              style={{ left: inlineField.left, top: inlineField.top, width: inlineField.width }}
              onChange={(e) => typeNote(inlineMark.id, e.target.value)}
              onBlur={() => {
                settle();
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
            />
          )}

          {/* Floating toolbar, its hint, and the notes list. */}
          <div
            className={cn(
              "absolute left-1/2 flex -translate-x-1/2 items-center gap-2",
              toolbarAt === "top" ? "top-3 flex-col" : "bottom-3 flex-col-reverse",
            )}
          >
            <MarkupToolbar
              tool={tool}
              onTool={pickTool}
              color={color}
              onColor={pickColor}
              history={{
                canUndo: canUndo(history),
                canRedo: canRedo(history),
                hasMarks: history.present.length > 0,
                hasSelection: selectedId !== null,
              }}
              onUndo={doUndo}
              onRedo={doRedo}
              onDelete={deleteSelected}
              onClear={clearAll}
              notes={{
                count: textMarks.length,
                open: notesOpen,
                onToggle: () => setNotesOpen((o) => !o),
              }}
              position={toolbarAt}
              onTogglePosition={() => setToolbarAt((p) => (p === "top" ? "bottom" : "top"))}
              onCancel={onCancel}
              onDone={finish}
            />
            <p className="rounded-md bg-card/90 px-2 py-0.5 text-xs text-muted-foreground shadow-xs">
              {activeHint}
            </p>
            {notesOpen && (
              <div className="w-80 max-w-[calc(100vw-1rem)] rounded-lg border border-border bg-card p-3 shadow-lg">
                <h3 className="text-sm font-semibold text-heading">Notes on the image</h3>
                {textMarks.length === 0 ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Choose Text and click the image to add a note.
                  </p>
                ) : (
                  <ol className="mt-2 flex max-h-60 flex-col gap-2 overflow-y-auto">
                    {textMarks.map((m, i) => (
                      <li key={m.id} className="flex items-center gap-1.5">
                        <span
                          aria-hidden
                          className="size-3 shrink-0 rounded-full"
                          style={{ background: penVar(m.color) }}
                        />
                        <Input
                          aria-label={`Note ${i + 1}`}
                          value={m.text}
                          maxLength={500}
                          placeholder={EMPTY_NOTE}
                          className="h-8"
                          onFocus={() => {
                            if (editingRef.current?.id === m.id) return;
                            startEditingText(m.id, settle(), "list");
                          }}
                          onChange={(e) => typeNote(m.id, e.target.value)}
                          onBlur={() => {
                            settle();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              e.currentTarget.blur();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          size="icon-sm"
                          variant="ghost"
                          aria-label={`Delete note ${i + 1}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => deleteNote(m.id)}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
